/**
 * Google Tag Manager
 * Handles Google tag (gtag.js) integration with Google Consent Mode v2.
 * Loads on ANY affirmative consent choice and signals granted/denied accordingly:
 *   - "Accept All"     → consent granted (full tracking)
 *   - "Essential Only" → consent denied (cookieless/modeled pings, no user identifiers)
 * One gtag.js instance feeds two destinations:
 *   - Google Ads (AW-…): purchase conversion (existing behavior, unchanged)
 *   - GA4 (G-…): full funnel mapping + cross-platform user_id stitching + Enhanced Conversions
 * Subscribes to StatsManager and maps funnel events onto GA4 standard events.
 */

import type { ConsentManager } from '../types/analytics.types';
import type { StatsManager } from '../core/stats-manager';
import type { EventName, EventData, EventMetadata } from '../types/events.types';
import { hashEmailToUserId, normalizeEmail } from '../core/user-id';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface GoogleTagDeps {
  consentManager: ConsentManager;
  statsManager: StatsManager;
}

const GTAG_ID = 'AW-17949376899';
const CONVERSION_LABEL = 'AW-17949376899/jfraCJLxrPcbEIOD9-5C';

// GA4 web stream "Web2App" (vocalist.learn2sing.us). Single measurement ID shared across
// sandbox + production, mirroring how the AW- Ads ID is shared. Lives here next to the Ads
// IDs by convention (Google tag IDs are co-located in this integration, not in config.ts).
const GA4_MEASUREMENT_ID = 'G-DPCEZC0ML1';

// const CONVERSION_PAGE_LABELS = [
//   'AW-17949376899/h0UqCIvv0YYcEIOD9-5C',
//   'AW-17949376899/kPhOCM3Jx4YcEIOD9-5C'
// ];

class GoogleTagManager {
  private initialized = false;
  private deps: Partial<GoogleTagDeps> = {};

  // Whether the user granted FULL consent ("Accept All"). Drives Consent Mode (granted/denied)
  // and gates user-provided identifiers (Enhanced Conversions email + user_id) on denied.
  private consentGranted = false;

  // SHA-256(email) user_id for GA4 web↔app stitching. Resolves once email is captured;
  // the purchase handler awaits it so user_id is always set before the conversion.
  private userId: string | null = null;
  private userIdPromise: Promise<string> | null = null;

  // Last email we reported a lead for — de-dupes re-submits and event replay so
  // generate_lead / user_id hashing don't fire twice for the same person.
  private lastReportedEmail: string | null = null;

  /**
   * Initialize Google Tag Manager with dependencies
   */
  init(deps: Partial<GoogleTagDeps>): void {
    if (!deps.consentManager || !deps.statsManager) {
      throw new Error(
        'GoogleTagManager.init() requires consentManager and statsManager dependencies'
      );
    }

    this.deps = deps;

    console.log('Google Tag Manager initializing...');

    // Load on ANY affirmative consent choice ("Accept All" OR "Essential Only", or where consent
    // isn't required). We always load gtag and signal Consent Mode v2 (granted vs denied) so that
    // declining users still produce cookieless/modeled pings instead of no data at all.
    this.deps.consentManager!.onEssentialConsentGranted(() => {
      this.loadGtag();
      this.deps.statsManager!.subscribe(this.handleStatsManagerEvent.bind(this));
      console.log('Google Tag subscribed to StatsManager events');
    });
  }

  /**
   * Dynamically load gtag.js and configure it
   */
  private loadGtag(): void {
    if (this.initialized) {
      return;
    }

    // Full consent ("Accept All") → granted; "Essential Only" → denied (still load, cookieless).
    this.consentGranted = !!this.deps.consentManager?.hasConsent();
    const consentState: 'granted' | 'denied' = this.consentGranted ? 'granted' : 'denied';

    try {
      // Dynamically load the gtag.js script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
      document.head.appendChild(script);

      // Google Consent Mode v2 — MUST be set before any config so it governs every hit.
      // denied = cookieless: Google receives only modeled/aggregated pings, stores no cookies,
      // and we additionally withhold all user-provided identifiers (see handleEmailCaptured).
      window.gtag('consent', 'default', {
        ad_storage: consentState,
        analytics_storage: consentState,
        ad_user_data: consentState,
        ad_personalization: consentState,
      });

      // Configure gtag (dataLayer stub and gtag function already defined in HTML)
      window.gtag('js', new Date());
      window.gtag('config', GTAG_ID);
      // GA4: same gtag.js instance, second destination. send_page_view:false because this is
      // an SPA — we emit page_view per screen ourselves (screen_viewed) to avoid double counts.
      window.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });

      this.initialized = true;
      console.log('Google tag loaded; Consent Mode:', consentState, '·', GTAG_ID, GA4_MEASUREMENT_ID);
    } catch (error) {
      console.error('Failed to load Google tag:', error);
    }
  }

  /**
   * Handle events from StatsManager — maps the funnel onto GA4 standard events.
   * Google Ads behavior is unchanged (only the purchase conversion).
   */
  private handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    _eventMetadata: EventMetadata,
    _value: string | number | null
  ): void {
    if (!this.initialized) {
      return;
    }

    try {
      switch (eventName) {
        case 'screen_viewed':
          this.sendGa4PageView(eventData);
          break;
        case 'email_captured':
          this.handleEmailCaptured(eventData);
          break;
        case 'paywall_viewed':
          this.sendGa4Event('view_item', {
            items: [{ item_name: (eventData.plan as string) || 'Vocalist Subscription' }],
          });
          break;
        case 'checkout_initiated':
          this.sendGa4Event('begin_checkout', {
            value: (eventData.price as number) || 0,
            currency: (eventData.currency as string) || 'USD',
            items: [
              {
                item_name: (eventData.plan as string) || 'Vocalist Subscription',
                price: (eventData.price as number) || 0,
                quantity: 1,
              },
            ],
          });
          break;
        case 'subscription_completed':
          // async (awaits user_id) — guard the fire-and-forget against unhandled rejection.
          this.reportPurchaseConversion(eventData).catch((error) =>
            console.error('Google Tag: purchase conversion failed:', error)
          );
          break;
      }
    } catch (error) {
      console.error('Google Tag: Failed to handle event:', eventName, error);
    }
  }

  /**
   * On email capture: feed Enhanced Conversions (raw email, Google hashes it) and set the
   * GA4 user_id (SHA-256 hash) for web↔app stitching. Fires before the paywall, so user_id
   * is in place ahead of the purchase conversion.
   */
  private handleEmailCaptured(eventData: EventData): void {
    const email = normalizeEmail(String(eventData.email || ''));
    if (!email || email === this.lastReportedEmail) {
      // Ignore empty, and de-dupe the same email arriving again (re-submit or replay to a
      // late subscriber) so generate_lead and the user_id hash don't fire twice.
      return;
    }
    this.lastReportedEmail = email;

    // Without full consent (Consent Mode denied) we must NOT send user-provided identifiers.
    // Still record the lead — Google models it cookielessly — but skip user_data + user_id.
    if (!this.consentGranted) {
      this.sendGa4Event('generate_lead', {});
      return;
    }

    // Enhanced Conversions / Customer Match: pass the raw normalized email; Google hashes it.
    window.gtag('set', 'user_data', { email });

    // Cross-platform user_id stitching: SHA-256(email) → set on GA4 before any conversion.
    this.userIdPromise = hashEmailToUserId(email)
      .then((uid) => {
        this.userId = uid;
        // Set user_id globally for all subsequent GA4 hits via gtag('set') ONLY. A second
        // gtag('config', GA4, …) would re-fire an automatic page_view (send_page_view defaults
        // to true per config call) and re-initialize the stream — so we avoid it here.
        window.gtag('set', { user_id: uid });
        return uid;
      })
      .catch((error) => {
        console.error('Google Tag: failed to compute GA4 user_id:', error);
        return '';
      });

    // Fire the lead conversion only after user_id is set so it attributes to the unified user
    // (the hash is async; the chain above is always-fulfilled via its .catch).
    this.userIdPromise.then(() => this.sendGa4Event('generate_lead', {}));
  }

  /**
   * Emit a GA4 page_view for the current SPA screen.
   */
  private sendGa4PageView(eventData: EventData): void {
    const screenHash = (eventData.screenHash as string) || '';
    window.gtag('event', 'page_view', {
      send_to: GA4_MEASUREMENT_ID,
      page_title: (eventData.screen as string) || '',
      // SPA uses hash routing — prefix '#' so page_path matches window.location (e.g. '#checkout').
      page_path: screenHash ? `#${screenHash}` : window.location.hash || '/',
      page_location: window.location.href,
    });
  }

  /**
   * Emit a generic GA4 event scoped to the GA4 destination only.
   */
  private sendGa4Event(eventName: string, params: Record<string, unknown>): void {
    window.gtag('event', eventName, {
      send_to: GA4_MEASUREMENT_ID,
      ...params,
    });
  }

  /**
   * Report the purchase to Google Ads (conversion, unchanged) AND GA4 (purchase).
   * Awaits the pending user_id hash so GA4 attributes the conversion to the stitched user.
   */
  private async reportPurchaseConversion(data: EventData): Promise<void> {
    if (this.userIdPromise) {
      try {
        await this.userIdPromise; // ensure GA4 user_id is set before the conversion
      } catch {
        /* report anyway */
      }
    }

    // Google Ads conversion (existing behavior — preserved verbatim).
    window.gtag('event', 'conversion', {
      send_to: CONVERSION_LABEL,
      value: (data.amount as number) || 1.0,
      currency: (data.currency as string) || 'USD',
      transaction_id: (data.transactionId as string) || '',
    });
    console.log('Google Ads purchase conversion reported:', data.transactionId);

    // GA4 purchase event.
    const value = (data.amount as number) || 0;
    const plan = (data.plan as string) || 'subscription';
    window.gtag('event', 'purchase', {
      send_to: GA4_MEASUREMENT_ID,
      transaction_id: (data.transactionId as string) || '',
      value,
      currency: (data.currency as string) || 'USD',
      items: [
        {
          item_id: plan,
          item_name: (data.plan as string) || 'Vocalist Subscription',
          price: value,
          quantity: 1,
        },
      ],
    });
    console.log('GA4 purchase reported:', data.transactionId, 'user_id:', this.userId);
  }
}

// Create singleton instance
export const googleTagManager = new GoogleTagManager();
